import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Github, Instagram, Facebook, Mail, ArrowUp } from 'lucide-react';
import WhatsAppIcon from './ui/WhatsAppIcon';
import { socialLinks, personalInfo } from '../data/mock';

const iconMap = {
  Linkedin,
  Github,
  Instagram,
  Facebook,
  MessageCircle: WhatsAppIcon
};

export const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-background border-t border-foreground/5 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-600/5 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Main Footer Content */}
        <div className="py-16 grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="text-2xl font-light tracking-tight group inline-block mb-1">
              <span className="text-foreground group-hover:text-yellow-600 dark:group-hover:text-yellow-400 transition-colors duration-300">
                Aadarsh
              </span>
              <span className="text-purple-600 dark:text-purple-500 group-hover:text-yellow-600 dark:group-hover:text-yellow-400 transition-colors duration-300">
                .
              </span>
            </Link>
            <p className="text-purple-600 dark:text-purple-500 font-medium mb-4">
              Freelance Digital Marketer In Thiruvalla
            </p>
            <p className="text-foreground/60 font-light leading-relaxed mb-6">
              {personalInfo.tagline}
            </p>
            <a
              href={`mailto:${personalInfo.email}?subject=Hi,%20I%20recently%20came%20across%20your%20website%20and%20wanted%20to%20connect.`}
              className="inline-flex items-center gap-2 text-foreground/70 hover:text-yellow-600 dark:hover:text-yellow-400 transition-colors duration-300 font-light"
            >
              <Mail size={16} />
              {personalInfo.email}
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-foreground font-light text-lg mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {['Home', 'About', 'Services', 'Blog'].map((item) => (
                <li key={item}>
                  <button
                    onClick={() => scrollToSection(item.toLowerCase())}
                    className="text-foreground/60 hover:text-yellow-600 dark:hover:text-yellow-400 transition-colors duration-300 font-light"
                  >
                    {item}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h3 className="text-foreground font-light text-lg mb-6">Services</h3>
            <ul className="space-y-3">
              <li>
                <button className="text-foreground/60 hover:text-yellow-600 dark:hover:text-yellow-400 transition-colors duration-300 font-light text-left">
                  Web Development
                </button>
              </li>
              <li>
                <button className="text-foreground/60 hover:text-yellow-600 dark:hover:text-yellow-400 transition-colors duration-300 font-light text-left">
                  Mobile Development
                </button>
              </li>
              <li>
                <button className="text-foreground/60 hover:text-yellow-600 dark:hover:text-yellow-400 transition-colors duration-300 font-light text-left">
                  SEO Optimization
                </button>
              </li>
              <li>
                <button className="text-foreground/60 hover:text-yellow-600 dark:hover:text-yellow-400 transition-colors duration-300 font-light text-left">
                  Digital Marketing
                </button>
              </li>
            </ul>
          </div>

          {/* Connect Column */}
          <div>
            <h3 className="text-foreground font-light text-lg mb-6">Connect</h3>
            <div className="grid grid-cols-3 gap-3">
              {socialLinks.map((social) => {
                const IconComponent = iconMap[social.icon];
                return (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-foreground/5 border border-foreground/5 rounded-xl flex items-center justify-center hover:border-purple-500/30 hover:scale-110 transition-all duration-300 group"
                    aria-label={social.name}
                  >
                    <IconComponent className="text-foreground/60 group-hover:text-yellow-600 dark:group-hover:text-yellow-400 transition-colors duration-300" size={18} />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom Bar — 3 columns: empty | copyright (centre) | links + scroll-top (right) */}
        <div className="border-t border-foreground/5 py-8 grid grid-cols-1 md:grid-cols-3 items-center gap-4">
          {/* Left — empty spacer on desktop */}
          <div className="hidden md:block" />

          {/* Centre — copyright */}
          <p className="text-foreground/40 text-sm font-light text-center">
            © {new Date().getFullYear()} {personalInfo.name}. All rights reserved.
          </p>

          {/* Right — links + scroll-to-top */}
          <div className="flex items-center justify-center md:justify-end gap-6">
            <button className="text-foreground/40 hover:text-yellow-600 dark:hover:text-yellow-400 text-sm font-light transition-colors duration-300">
              Privacy Policy
            </button>
            <button className="text-foreground/40 hover:text-yellow-600 dark:hover:text-yellow-400 text-sm font-light transition-colors duration-300">
              Terms of Service
            </button>
            <button
              onClick={scrollToTop}
              className="w-10 h-10 bg-foreground/5 border border-foreground/5 rounded-lg flex items-center justify-center hover:border-purple-500/30 hover:scale-110 transition-all duration-300 group"
              aria-label="Scroll to top"
            >
              <ArrowUp className="text-foreground/60 group-hover:text-yellow-600 dark:group-hover:text-yellow-400 transition-colors duration-300" size={18} />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};
